import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Container,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Description,
  Download,
  CloudUpload,
  Close,
  ThumbUp,
  Menu as MenuIcon,
  PictureAsPdf,
  Print,
  Share,
  Save,
  VerifiedUser,
  Lock,
  Assignment,
} from "@mui/icons-material";

function Agreement() {
  const [currentPage, setCurrentPage] = useState(1);
  const [checked, setChecked] = useState(false);
  const [signature, setSignature] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [bytecodeimage2, setBytecodeimage2] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  const totalPages = 2;
  const signatureInputRef = useRef(null);
  const thumbInputRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const agreementData = [
    {
      unit: "10th",
      monthName: "March",
      date: "Twenty Five",
      employeeName: "John Doe",
      employeeFathersName: "Richard Doe",
      age: 30,
      presentAddress: "123, ABC Street, Hyderabad",
      permanentAddress: "456, XYZ Road, Bangalore",
      serviceCommitment: 2,
      custodyOf: "ID Proofs and Bank Cheque",
      serviceBreakAmount: 50000,
      agreementDate: "08/02/2025",
      employeeId: "EMP-2025-001",
      department: "Software Development",
      designation: "Senior Developer",
    },
  ];

  const steps = ['Agreement Review', 'Terms Acceptance', 'Signature'];

  const handleChangeC = (event) => {
    setChecked(event.target.checked);
  };

  const handleFileUpload = (type) => {
    if (type === 'signature') {
      signatureInputRef.current?.click();
    } else {
      thumbInputRef.current?.click();
    }
  };

  const selectFileSign2 = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showSnackbar("File size must be less than 2MB", "error");
        return;
      }
      
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        showSnackbar("Please upload JPEG, PNG or JPG files only", "error");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile(file);
        setBytecodeimage2(reader.result.split(',')[1]);
        setSignature(true);
        showSnackbar(`${type === 'signature' ? 'Signature' : 'Thumb impression'} uploaded successfully`, "success");
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      scrollToTop();
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      if (currentPage === 1 && !checked) {
        showSnackbar("Please accept the terms and conditions", "warning");
        return;
      }
      setCurrentPage(currentPage + 1);
      scrollToTop();
    }
  };

  const scrollToTop = () => {
    setTimeout(() => {
      const topElement = document.getElementById("agreement-top");
      if (topElement) {
        topElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const closeImage = () => {
    setSignature(false);
    setSelectedFile(null);
    setBytecodeimage2(null);
  };

  const handleSubmitAgreement = () => {
    if (!signature) {
      showSnackbar("Please upload your signature and thumb impression", "warning");
      return;
    }
    
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setAgreed(true);
      showSnackbar("Agreement submitted successfully!", "success");
    }, 1500);
  };

  const handleDownloadPDF = () => {
    setGeneratingPDF(true);
    showSnackbar("Generating PDF document...", "info");
    
    // Simulate PDF generation
    setTimeout(() => {
      setGeneratingPDF(false);
      showSnackbar("PDF generated successfully! Downloading...", "success");
      
      // Create a temporary link for download
      const link = document.createElement('a');
      link.href = '#';
      link.download = `Service-Agreement-${agreementData[0].employeeName}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 2000);
  };

  const handlePrint = () => {
    showSnackbar("Opening print preview...", "info");
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const renderPageNumbers = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <Tooltip key={page} title={`Page ${page}`}>
            <Box
              onClick={() => {
                if (page === 2 && !checked) {
                  showSnackbar("Please accept the terms and conditions first", "warning");
                  return;
                }
                setCurrentPage(page);
                scrollToTop();
              }}
              sx={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                cursor: 'pointer',
                backgroundColor: currentPage === page ? theme.palette.primary.main : theme.palette.grey[100],
                color: currentPage === page ? 'white' : theme.palette.text.primary,
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
                border: `1px solid ${currentPage === page ? theme.palette.primary.main : theme.palette.grey[300]}`,
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: theme.shadows[2],
                  backgroundColor: currentPage === page ? theme.palette.primary.dark : theme.palette.grey[200],
                },
              }}
            >
              {page}
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  const renderActionButtons = () => (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 3 }}>
      <Button
        variant="contained"
        startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
        onClick={handleDownloadPDF}
        disabled={generatingPDF}
        sx={{
          flex: 1,
          backgroundColor: generatingPDF ? theme.palette.grey[500] : '#d32f2f',
          '&:hover': {
            backgroundColor: generatingPDF ? theme.palette.grey[500] : '#b71c1c',
          },
        }}
      >
        {generatingPDF ? 'Generating PDF...' : 'Generate & Download PDF'}
      </Button>
      
      <Button
        variant="outlined"
        startIcon={<Print />}
        onClick={handlePrint}
        sx={{ flex: 1 }}
      >
        Print Agreement
      </Button>
      
      <Button
        variant="outlined"
        startIcon={<Save />}
        onClick={() => showSnackbar("Save feature coming soon!", "info")}
        sx={{ flex: 1 }}
      >
        Save Draft
      </Button>
      
      <Button
        variant="outlined"
        startIcon={<Share />}
        onClick={() => showSnackbar("Share feature coming soon!", "info")}
        sx={{ flex: 1 }}
      >
        Share
      </Button>
    </Box>
  );

  const renderEmployeeInfoCard = () => (
    <Card sx={{ mb: 3, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 60, height: 60 }}>
              <Assignment fontSize="large" />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h6" gutterBottom>
              Employee Information
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Name: <b>{agreementData[0].employeeName}</b>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Employee ID: <Chip label={agreementData[0].employeeId} size="small" />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Department: <b>{agreementData[0].department}</b>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Designation: <b>{agreementData[0].designation}</b>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Chip 
              icon={<VerifiedUser />} 
              label="Agreement Status" 
              color={agreed ? "success" : "warning"}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderSignatureUpload = () => (
    <Box sx={{ mt: 4 }}>
      <Card sx={{ mb: 3, border: `1px solid ${theme.palette.primary.main}20` }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock fontSize="small" />
            Digital Signature & Thumb Impression
          </Typography>
          
          <Grid container spacing={3}>
            {/* Signature Upload */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Description fontSize="small" />
                    Signature Upload
                  </Typography>
                  {!signature ? (
                    <Box
                      sx={{
                        border: `2px dashed ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.dark,
                        },
                      }}
                      onClick={() => handleFileUpload('signature')}
                    >
                      <CloudUpload sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Click to upload signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Supports: JPEG, PNG, JPG (Maximum 2MB)
                      </Typography>
                      <input
                        type="file"
                        ref={signatureInputRef}
                        style={{ display: 'none' }}
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={(e) => selectFileSign2(e, 'signature')}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative', textAlign: 'center' }}>
                      <Box sx={{ p: 2, border: `1px solid ${theme.palette.success.main}`, borderRadius: 2, backgroundColor: theme.palette.success.light + '20' }}>
                        <img
                          src={`data:image/jpg;base64,${bytecodeimage2}`}
                          alt="Signature"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 120,
                            borderRadius: 4,
                          }}
                        />
                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                          ✓ Signature uploaded successfully
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={closeImage}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: theme.palette.error.main,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.dark,
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Thumb Impression Upload */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ThumbUp fontSize="small" />
                    Left Hand Thumb Impression
                  </Typography>
                  {!signature ? (
                    <Box
                      sx={{
                        border: `2px dashed ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.dark,
                        },
                      }}
                      onClick={() => handleFileUpload('thumb')}
                    >
                      <ThumbUp sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Click to upload thumb impression
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Supports: JPEG, PNG, JPG (Maximum 2MB)
                      </Typography>
                      <input
                        type="file"
                        ref={thumbInputRef}
                        style={{ display: 'none' }}
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={(e) => selectFileSign2(e, 'thumb')}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative', textAlign: 'center' }}>
                      <Box sx={{ p: 2, border: `1px solid ${theme.palette.success.main}`, borderRadius: 2, backgroundColor: theme.palette.success.light + '20' }}>
                        <img
                          src={`data:image/jpg;base64,${bytecodeimage2}`}
                          alt="Thumb Impression"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 120,
                            borderRadius: 4,
                          }}
                        />
                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                          ✓ Thumb impression uploaded successfully
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={closeImage}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: theme.palette.error.main,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.dark,
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
            Note: Your signature and thumb impression will be securely stored and used only for this agreement.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  const renderProgressStepper = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stepper activeStep={currentPage - 1} alternativeLabel sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {renderPageNumbers()}
          <Typography variant="caption" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.grey[50], py: 3 }}>
      <Container maxWidth="lg" id="agreement-top">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.dark }}>
                  <Description sx={{ mr: 2, verticalAlign: 'middle' }} />
                  Service Agreement
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Envision Integrated Services Private Limited
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This is a legally binding agreement. Please read all terms carefully before accepting.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 1 }}>
                  <Chip 
                    icon={<VerifiedUser />} 
                    label={agreed ? "Agreement Signed" : "Pending Signature"} 
                    color={agreed ? "success" : "warning"}
                    variant="outlined"
                    size="medium"
                  />
                  <Chip 
                    label={`V2.1`} 
                    color="info"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Action Buttons */}
        {renderActionButtons()}

        {/* Employee Information */}
        {renderEmployeeInfoCard()}

        {/* Progress Stepper */}
        {renderProgressStepper()}

        {/* Main Agreement Content */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Content Header */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.dark,
              color: 'white',
              p: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {currentPage === 1 ? "Service Agreement Terms" : "Non-Disclosure Agreement"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
              {currentPage === 1 
                ? "Terms and conditions governing your employment and service commitment"
                : "Confidentiality and non-disclosure obligations for company information"}
            </Typography>
          </Box>

          {/* Content Body */}
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {agreementData.map((data, index) => (
              <Zoom in={true} key={index}>
                <Box sx={{ letterSpacing: '0.2px' }}>
                  {currentPage === 1 && (
                    <>
                      <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.8 }}>
                        THIS AGREEMENT made at Hyderabad on <b>{data.unit}</b> day
                        of <b>{data.monthName}</b>, Two thousand and{" "}
                        <b>{data.date}</b>. BETWEEN
                        <b> {data.employeeName}</b>, S/o, D/o{" "}
                        <b>{data.employeeFathersName}</b>, aged <b>{data.age}</b>{" "}
                        years, residing at <b>{data.presentAddress}</b> and having
                        permanent address at <b>{data.permanentAddress}</b>.
                      </Typography>

                      <Box sx={{ textAlign: 'center', my: 4 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                          AND
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                      </Box>

                      <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.8 }}>
                        Envision Integrated Services Private Limited, a company
                        incorporated under the Companies Act 1956 and having its
                        Corporate Office at #913, 9th Floor, Manjeera Trinity
                        Corporate Building, E-Seva Lane, KPHB, Hyderabad – 500085,
                        Reg. Office at #8-3-940/5/501, Tirumala Apartments,
                        Yellareddyguda Cross Roads, Ameerpet, Hyderabad, Telangana
                        – 500038 (hereinafter referred to as "Employer").
                      </Typography>

                      <Card variant="outlined" sx={{ my: 4, borderColor: theme.palette.primary.main }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              textAlign: 'center',
                              fontWeight: 'bold',
                              color: theme.palette.primary.dark,
                              mb: 3,
                            }}
                          >
                            IT IS NOW HEREBY AGREED AS UNDER:
                          </Typography>
                          
                          <Box sx={{ pl: { xs: 0, sm: 2 } }}>
                            {[
                              `In consideration of the profession to be imparted by the Employer, the Employee undertakes irrevocably to serve the Employer for a minimum period of ${data.serviceCommitment} year(s) from the date of joining.`,
                              `As a guarantee, the Employee agrees to keep the following original documents & Bank Cheques with the custody of the Employer: ${data.custodyOf}.`,
                              `If the Employee resigns within the agreed period, they shall be liable to pay liquidated damages amounting to Rs ${data.serviceBreakAmount.toLocaleString("en-IN")} /- (Rupees ${data.serviceBreakAmount.toLocaleString("en-IN")} Only).`,
                              `In case of an unauthorized absence of more than three consecutive days, the Employer can claim the employee's cheque no.`,
                            ].map((text, idx) => (
                              <Box key={idx} sx={{ display: 'flex', mb: 2 }}>
                                <Typography variant="body2" sx={{ mr: 1, color: theme.palette.primary.main, fontWeight: 'bold' }}>
                                  {idx + 1}.
                                </Typography>
                                <Typography variant="body1" sx={{ textAlign: 'justify', flex: 1 }}>
                                  {text}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* Terms Acceptance */}
                      <Card sx={{ mt: 4, backgroundColor: theme.palette.grey[50] }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={handleChangeC}
                                color="primary"
                                size="large"
                              />
                            }
                            label={
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                I have read, understood, and agree to all the terms and conditions mentioned above
                              </Typography>
                            }
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 6 }}>
                            By checking this box, you acknowledge that you accept all the terms of this service agreement.
                          </Typography>
                        </CardContent>
                      </Card>
                    </>
                  )}

                  {currentPage === 2 && (
                    <>
                      <Card variant="outlined" sx={{ mb: 4, borderColor: theme.palette.info.main }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              textAlign: 'center',
                              fontWeight: 'bold',
                              mb: 3,
                              color: theme.palette.info.dark,
                            }}
                          >
                            IN WITNESS WHEREOF, THIS AGREEMENT TO BE EXECUTED AS DATE FIRST MENTIONED ABOVE.
                          </Typography>

                          <Typography variant="body1" paragraph sx={{ textAlign: 'justify', mb: 3, lineHeight: 1.8 }}>
                            I solely accept to submit my original documents & Bank
                            Cheques for the period of my employment with the company.
                            The company or any individual has never forced or mandated
                            me to submit them, but it is limited to security towards
                            my agreed service tenure with the company.
                          </Typography>
                        </CardContent>
                      </Card>

                      <Card sx={{ mb: 4 }}>
                        <CardContent>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center', color: theme.palette.primary.dark }}>
                            NON-DISCLOSURE AGREEMENT
                          </Typography>
                          
                          <Box sx={{ pl: { xs: 0, sm: 2 } }}>
                            {[
                              "I am not involved in any civil/criminal cases, proceedings, charges, or enquiry before joining Envision Integrated Services Pvt. Ltd.",
                              "I am not involved in any disciplinary actions or malpractices before joining Envision Integrated Services Pvt. Ltd.",
                              "I was not employed gainfully or on an honorary basis in any organization before joining Envision Integrated Services Pvt. Ltd.",
                              "I will protect confidential information and will not share it without proper authorization.",
                              "Any unintentional breach of this policy will be investigated and may be punished appropriately.",
                              "I will abide by all company policies, rules, and regulations currently prevailing or established in the future.",
                              "I will not approach any of the company's clients, vendors, or associates for two years after termination/resignation.",
                              "The company may terminate this agreement with one month's notice if I commit any material or persistent breach of provisions or am guilty of any misconduct or neglect affecting the company's business.",
                              "I shall keep all proprietary information confidential in accordance with the NDA.",
                              "If any statement in this agreement is found false, the company has the right to terminate my services and take legal action.",
                              "I will indemnify and protect the company from any loss or liability caused by my actions.",
                              "I affirm that all the above statements are true to my knowledge and belief.",
                            ].map((item, idx) => (
                              <Box key={idx} sx={{ display: 'flex', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ mr: 1.5, color: theme.palette.primary.main, fontWeight: 'bold', minWidth: 24 }}>
                                  {idx + 1}.
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'justify', flex: 1 }}>
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          
                          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  <b>Name of the Employee:</b> {data.employeeName}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  <b>Date:</b> {data.agreementDate}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </CardContent>
                      </Card>

                      {/* Signature Upload Section */}
                      {renderSignatureUpload()}
                    </>
                  )}
                </Box>
              </Zoom>
            ))}
          </Box>

          {/* Footer Navigation */}
          <Box
            sx={{
              p: 3,
              backgroundColor: theme.palette.grey[50],
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  sx={{ minWidth: 120 }}
                >
                  Previous
                </Button>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {renderPageNumbers()}
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                {currentPage === totalPages ? (
                  <Button
                    fullWidth={isMobile}
                    variant="contained"
                    color="success"
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                    onClick={handleSubmitAgreement}
                    disabled={loading || !signature}
                    sx={{ minWidth: 150 }}
                  >
                    {loading ? 'Processing...' : 'Submit Agreement'}
                  </Button>
                ) : (
                  <Button
                    fullWidth={isMobile}
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={handleNext}
                    disabled={currentPage === totalPages || !checked}
                    sx={{ minWidth: 120 }}
                  >
                    Next
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Stats Card */}
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {totalPages}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Pages
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color={checked ? "success.main" : "text.secondary"} sx={{ fontWeight: 'bold', mb: 1 }}>
                    {checked ? '✓' : '○'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Terms Accepted
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color={signature ? "success.main" : "text.secondary"} sx={{ fontWeight: 'bold', mb: 1 }}>
                    {signature ? '✓' : '○'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Documents Uploaded
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Agreement;